import type { Editor } from "@tiptap/react";
import { cn } from "cn";
import { memo, useCallback } from "react";
import { Button } from "@/components/ui/button/button";
import { useEditorIsActive } from "@/components/ui/editor/core";
import {
  BwBoldLine,
  BwBulletedListLine,
  BwClearTextLine,
  BwItalicLine,
  BwLinkLine,
  BwNumberedListLine,
  BwStrikethroughLine,
} from "@/components/ui/icons";

const FormatIcon = ({ format }: { format: string }) => {
  switch (format) {
    case "bold":
      return <BwBoldLine className="size-4" />;
    case "italic":
      return <BwItalicLine className="size-4" />;
    case "strike":
      return <BwStrikethroughLine className="size-4" />;
    case "orderedList":
      return <BwNumberedListLine className="size-4" />;
    case "bulletList":
      return <BwBulletedListLine className="size-4" />;
    case "link":
      return <BwLinkLine className="size-4" />;
    case "clear":
      return <BwClearTextLine className="size-4" />;
    default:
      return null;
  }
};

export const FormatButton = memo(function FormatButton({
  format,
  editor,
}: {
  format: string;
  editor: Editor;
}) {
  const isActive = useEditorIsActive(editor, format);

  const handleClick = useCallback(() => {
    if (!editor) return;

    switch (format) {
      case "bold":
        editor.chain().focus().toggleBold().run();
        break;
      case "italic":
        editor.chain().focus().toggleItalic().run();
        break;
      case "strike":
        editor.chain().focus().toggleStrike().run();
        break;
      case "orderedList":
        editor.chain().focus().toggleOrderedList().run();
        break;
      case "bulletList":
        editor.chain().focus().toggleBulletList().run();
        break;
      case "link":
        editor.commands.toggleLinkDialog(true);
        break;
      case "clear":
        const plainText = editor.getHTML().replace(/<[^p].*?>/g, "");
        editor.commands.clearContent();
        editor.commands.insertContent(plainText);
        break;
      default:
        break;
    }
  }, [editor, format]);

  return (
    <Button
      data-test={format}
      size="icon-md"
      kind="ghost"
      variant="neutral"
      className={cn("rounded-lg", isActive && "bg-bg-dark-min")}
      onClick={handleClick}
    >
      <FormatIcon format={format} />
    </Button>
  );
});
